// Starter templates for the "Let AI self-configure" agent creation flow.
// Each template's `prompt` is dropped into the requirements field and becomes
// the first chat message to the self-configuring agent, so it is written as a
// natural-language brief grounded in real Workflow86 agent capabilities
// (web search, email, Slack, scheduling, calendar, running workflows, database
// tables, tasks and forms). Templates are grouped by operational role via the
// `category` field; the display order of those roles is AGENT_CREATION_TEMPLATE_CATEGORY_ORDER.

export const AGENT_CREATION_TEMPLATE_CATEGORY_ORDER = [
  "Sales",
  "Marketing",
  "Support",
  "HR",
  "Finance",
  "Legal",
  "IT",
  "Operations",
  "Productivity",
];

const AGENT_CREATION_TEMPLATES = [
  // ----- Sales -----
  {
    id: "lead-researcher",
    category: "Sales",
    title: "Lead research & qualifier",
    shortDescription:
      "Researches new leads on the web, enriches a database table, scores fit, and alerts sales.",
    prompt: `Create an inbound lead research and qualification agent.

Responsibilities:
- When a new lead arrives, use web search to research the person and their company (role, industry, size, recent news, and likely needs).
- Enrich and store the findings in a database table with consistent fields: name, company, role, industry, company size, fit score, and a short rationale.
- Score each lead's fit against our ideal customer profile and flag the strongest opportunities.
- Notify the sales team about high-fit leads via Slack or email with a concise summary and suggested next step.

Tools to enable: Web Search, Extract Webpage, Query Table, Create Row, Update Row, Send Email, Send Slack Component.

Behavior: be factual and cite where information came from, mark anything you could not verify as "unconfirmed," and ask me to define the ideal customer profile if it is not already clear.`,
  },
  {
    id: "sales-followup",
    category: "Sales",
    title: "Sales outreach sequencer",
    shortDescription:
      "Runs personalized follow-up sequences, logs activity, and nudges reps on stalled deals.",
    prompt: `Create a sales follow-up and outreach agent.

Responsibilities:
- For each prospect, schedule a multi-step follow-up sequence with personalized emails based on their stage and last interaction.
- Use web search to add a relevant, personalized touch to outreach when helpful.
- Log every touchpoint and response in a database table so the pipeline stays current.
- Detect stalled deals (no reply after N days) and nudge the owning rep via Slack or email with a suggested next action.

Tools to enable: Web Search, Query Table, Update Row, Schedule Message, Edit Scheduled Message, Send Email, Send Slack Component.

Behavior: keep messaging on-brand and not pushy, stop sequences when a prospect replies or opts out, and ask me for the sequence steps, timing, and tone before starting.`,
  },
  {
    id: "proposal-drafter",
    category: "Sales",
    title: "Proposal & quote drafter",
    shortDescription:
      "Assembles tailored proposals and quotes from deal data and routes them for approval.",
    prompt: `Create a proposal and quote drafting agent.

Responsibilities:
- Gather the deal details (requirements, scope, pricing inputs, timeline) from our database tables, forms, or the rep's brief.
- Draft a clear, on-brand proposal or quote tailored to the prospect, reusing standard sections from our reference files.
- Record each draft in a database table with version, status, and owner.
- Route the draft to the rep or manager for review via task or email, and follow up if it stalls.

Tools to enable: Get Form, Query Table, Create Row, Update Row, Read File, Search Agent Files, Generate Document, Review Task, Send Email.

Behavior: never send a proposal to a customer without internal approval, keep pricing consistent with our rules, and ask me for the pricing model and approval steps before drafting.`,
  },

  // ----- Marketing -----
  {
    id: "content-creator",
    category: "Marketing",
    title: "Content drafting agent",
    shortDescription:
      "Drafts on-brand blog posts, newsletters, and copy from a brief and routes for review.",
    prompt: `Create a marketing content drafting agent.

Responsibilities:
- Draft content (blog posts, newsletters, product copy) from a brief, matching our brand voice and style guide.
- Use web search to ground content in current facts and add relevant references.
- Store drafts in a database table with topic, channel, status, and owner.
- Route drafts for review via task or email and incorporate the feedback.

Tools to enable: Web Search, Extract Webpage, Query Table, Create Row, Update Row, Review Task, Send Email.

Behavior: never publish without human approval, flag any claims you could not verify, and ask me for the brand voice, channels, and review steps before drafting.`,
  },
  {
    id: "social-scheduler",
    category: "Marketing",
    title: "Social media planner",
    shortDescription:
      "Turns campaigns into a scheduled content calendar and reminds owners before publish.",
    prompt: `Create a social media planning agent.

Responsibilities:
- Turn campaign briefs and content into a schedule of posts across our channels.
- Draft post copy tailored to each channel and store the plan in a database table with channel, date, and status.
- Schedule reminders and create calendar entries for publish dates.
- Notify the owner via Slack or email when a post is due for review or publishing.

Tools to enable: Query Table, Create Row, Update Row, Schedule Message, Create Calendar Event, Send Slack Component, Send Email.

Behavior: never post publicly on your own, keep messaging on-brand, and ask me which channels, cadence, and approval flow to use before scheduling.`,
  },
  {
    id: "research-reporter",
    category: "Marketing",
    title: "Market research analyst",
    shortDescription:
      "Runs recurring web research on a topic or competitors and emails a digest on a schedule.",
    prompt: `Create a recurring market research and reporting agent.

Responsibilities:
- On a recurring schedule (for example weekly), use web search and webpage extraction to gather the latest developments on a topic, market, or set of competitors I specify.
- Summarize the most important findings into a clear, skimmable digest with sections for what's new, why it matters, and recommended actions.
- Store each report in a database table so we build a historical record over time.
- Email the digest to the distribution list at the same time each cycle.

Tools to enable: Web Search, Extract Webpage, Query Table, Create Row, Recurring Schedule, Send Email.

Behavior: prioritize credible sources, keep summaries concise and decision-oriented, and ask me to confirm the topics, recipients, and cadence before scheduling the recurring run.`,
  },

  // ----- Support -----
  {
    id: "support-triage",
    category: "Support",
    title: "Support triage agent",
    shortDescription:
      "Triages inbound support requests, drafts replies, updates task status, and escalates the hard ones.",
    prompt: `Create a customer support triage agent.

Responsibilities:
- Read incoming support requests and classify each one by topic, urgency, and sentiment.
- Draft a clear, empathetic first reply for routine questions, and ask clarifying questions when the request is ambiguous.
- Track each request as a task: plan the work, update the task status as it progresses, and close it out when resolved.
- Escalate anything high-priority, angry, or outside policy by sending a summary to the right person via email or Slack instead of answering directly.

Tools to enable: Plan Tasks, Update Task Queue, Review Task, Send Email, Send Slack Component.

Behavior: always confirm you have enough information before resolving a request, keep a professional and friendly tone, and never promise refunds or commitments that need human approval.`,
  },
  {
    id: "feedback-analyzer",
    category: "Support",
    title: "Feedback & survey analyzer",
    shortDescription:
      "Analyzes survey and review responses, tracks themes and sentiment, and reports trends.",
    prompt: `Create a customer feedback and survey analysis agent.

Responsibilities:
- Read incoming survey responses, reviews, and support feedback, then classify sentiment and tag recurring themes.
- Record each response in a database table with sentiment, theme, and any notable quotes.
- Detect emerging issues or spikes in negative sentiment and alert the right team via Slack or email.
- On a recurring schedule, compile a trends summary of top themes, sentiment shifts, and recommended actions.

Tools to enable: Query Table, Create Row, Update Row, Recurring Schedule, Send Slack Component, Send Email.

Behavior: stay objective, separate one-off complaints from real trends, and ask me what themes matter most and who should receive alerts and the summary.`,
  },
  {
    id: "kb-maintainer",
    category: "Support",
    title: "Knowledge base maintainer",
    shortDescription:
      "Spots content gaps from recurring questions, drafts help articles, and re-reviews stale ones.",
    prompt: `Create a knowledge base maintenance agent.

Responsibilities:
- Monitor recurring support questions and identify gaps or outdated articles in our knowledge base.
- Draft new or updated help articles grounded in our reference files and resolved tickets.
- Track each article in a database table with status, owner, and last-reviewed date.
- Route drafts for review and schedule periodic re-review of stale articles.

Tools to enable: Read File, Search Agent Files, Create Markdown File, Query Table, Create Row, Update Row, Review Task, Schedule Message.

Behavior: never publish unreviewed content, cite the source tickets, and ask me where the knowledge base lives and who approves changes.`,
  },

  // ----- HR -----
  {
    id: "recruiting-screener",
    category: "HR",
    title: "Recruiting screener & scheduler",
    shortDescription:
      "Screens applicants against criteria, scores candidates, and books interviews on the calendar.",
    prompt: `Create a recruiting screening and interview-scheduling agent.

Responsibilities:
- Review incoming applications and resumes, then score each candidate against the role's required and preferred criteria.
- Record each candidate in a database table with their score, key strengths, gaps, and a recommendation (advance, hold, or reject).
- For candidates who advance, propose interview times and create calendar events for the candidate and interviewers.
- Send candidates and hiring managers email updates about next steps.

Tools to enable: Query Table, Create Row, Update Row, Create Calendar Event, Send Email.

Behavior: stay objective and consistent across candidates, avoid bias by judging only against the stated criteria, and ask the hiring manager to confirm the criteria and interview panel before screening begins.`,
  },
  {
    id: "onboarding-buddy",
    category: "HR",
    title: "Employee onboarding agent",
    shortDescription:
      "Creates onboarding checklists, fans out setup tasks, and tracks new-hire readiness.",
    prompt: `Create an employee onboarding agent.

Responsibilities:
- When a new hire is added, create their onboarding record in a database table and a checklist of setup tasks.
- Fan out tasks to IT, facilities, and the manager, and track each to completion.
- Send the new hire a welcome email with first-day details, and schedule check-in reminders and calendar events.
- Flag overdue setup items to the right owner.

Tools to enable: Query Table, Create Row, Update Row, Plan Tasks, Update Task Queue, Send Email, Schedule Message, Create Calendar Event.

Behavior: confirm the start date and role details before kicking things off, avoid duplicate reminders, and ask me for the onboarding checklist and task owners.`,
  },
  {
    id: "leave-request",
    category: "HR",
    title: "Time-off request handler",
    shortDescription:
      "Intakes leave requests, validates against policy, and routes them for manager approval.",
    prompt: `Create a time-off and leave request agent.

Responsibilities:
- Read incoming leave requests from forms or email and extract dates, type, and reason.
- Validate against policy (notice period, balance, blackout dates) and record each request in a database table.
- Route to the manager for approval via task or email and notify the requester of the outcome.
- Schedule reminders for upcoming approved leave.

Tools to enable: Get Form, Query Table, Create Row, Update Row, Review Task, Send Email, Schedule Message.

Behavior: never approve leave on your own, flag policy conflicts, and ask me for the leave policy and approval chain before processing.`,
  },

  // ----- Finance -----
  {
    id: "ap-invoice-processor",
    category: "Finance",
    title: "Invoice processing agent",
    shortDescription:
      "Extracts invoice details, validates against POs, records them, and routes approvals.",
    prompt: `Create an accounts payable invoice processing agent.

Responsibilities:
- Read invoices arriving by email or form, then extract vendor, invoice number, amount, currency, due date, line items, and PO number.
- Validate the data (required fields present, totals add up, due date is sensible) and flag duplicates or mismatches against existing records.
- Write each invoice into a database table with status, approver, and extraction confidence, updating rather than duplicating existing entries.
- Route invoices for approval based on amount, notifying the right approver via task or email, and follow up as due dates approach.

Tools to enable: Send Email, Get Form, Query Table, Create Row, Update Row, Review Task, Schedule Message.

Behavior: never mark an invoice approved on your own, escalate anything that fails validation, and ask me for the approval thresholds and target table before processing.`,
  },
  {
    id: "expense-approver",
    category: "Finance",
    title: "Expense approval agent",
    shortDescription:
      "Reviews submitted expenses against policy, records them, and routes approvals by amount.",
    prompt: `Create an expense approval agent.

Responsibilities:
- Read submitted expenses from forms, receipts, or email and extract amount, category, date, and submitter.
- Validate against policy (limits, required receipts, allowed categories) and flag exceptions.
- Record each expense in a database table with status and approver.
- Route by amount for approval and notify the submitter of the result.

Tools to enable: Get Form, Query Table, Create Row, Update Row, Review Task, Send Email.

Behavior: never approve on your own, escalate policy exceptions, and ask me for the expense policy and thresholds before processing.`,
  },
  {
    id: "collections-followup",
    category: "Finance",
    title: "Receivables follow-up agent",
    shortDescription:
      "Tracks outstanding invoices, sends polite payment reminders, and escalates overdue accounts.",
    prompt: `Create an accounts receivable follow-up agent.

Responsibilities:
- Track outstanding invoices and their due dates from our database tables.
- Schedule and send polite payment reminder emails as due dates approach and pass.
- Log every reminder and response, and update invoice status as payments come in.
- Escalate seriously overdue accounts to the finance team via Slack or email.

Tools to enable: Query Table, Update Row, Schedule Message, Edit Scheduled Message, Send Email, Send Slack Component.

Behavior: keep a professional, courteous tone, never threaten or commit to terms, and ask me for the reminder cadence and escalation rules before starting.`,
  },

  // ----- Legal -----
  {
    id: "document-reviewer",
    category: "Legal",
    title: "Document & contract reviewer",
    shortDescription:
      "Reviews documents and contracts, summarizes key terms, flags risks, and routes for approval.",
    prompt: `Create a document and contract review agent.

Responsibilities:
- Read uploaded documents and contracts, summarize their purpose and structure, and pull out the key terms (parties, dates, obligations, amounts, termination, liability).
- Flag potential issues, missing clauses, and anything that deviates from our standard terms, clearly noting this is an initial analysis and not legal advice.
- Record each review in a database table with the document name, summary, risk flags, and recommendation.
- Route documents that need sign-off to the right approver via a task or email with the highlights they need to decide.

Tools to enable: Read File, Search Agent Files, Query Table, Create Row, Update Row, Review Task, Send Email.

Behavior: be precise and cite the specific clause when you raise a concern, never approve on your own, and ask me what our standard terms and red-line rules are before reviewing.`,
  },
  {
    id: "compliance-checker",
    category: "Legal",
    title: "Compliance & policy reviewer",
    shortDescription:
      "Checks documents and requests against policy, flags violations, and routes for sign-off.",
    prompt: `Create a compliance and policy review agent.

Responsibilities:
- Review submitted documents, requests, or content against our policies and applicable rules.
- Flag violations, missing approvals, and risky language, noting this is an initial check and not legal advice.
- Record each review in a database table with the item, findings, and risk level.
- Route anything that needs sign-off to the right owner via task or email.

Tools to enable: Query Table, Create Row, Update Row, Review Task, Send Email.

Behavior: cite the specific policy when you raise a flag, never clear an item on your own, and ask me which policies and reviewers apply before starting.`,
  },
  {
    id: "nda-intake",
    category: "Legal",
    title: "NDA & contract intake",
    shortDescription:
      "Intakes NDA and contract requests, flags non-standard terms, and routes to legal.",
    prompt: `Create an NDA and contract intake agent.

Responsibilities:
- Read incoming NDA and contract requests from forms or email and extract counterparty, type, term, and purpose.
- Check requests against our standard templates and flag non-standard terms.
- Record each request in a database table with status and assigned reviewer.
- Route to legal for review and notify the requester of next steps.

Tools to enable: Get Form, Query Table, Create Row, Update Row, Review Task, Send Email.

Behavior: never approve or sign on your own, highlight deviations from standard terms, and ask me for the standard templates and review chain before processing.`,
  },

  // ----- IT -----
  {
    id: "internal-helpdesk",
    category: "IT",
    title: "Internal helpdesk agent",
    shortDescription:
      "Answers staff questions from your knowledge base, files tickets, and escalates the rest.",
    prompt: `Create an internal IT and operations helpdesk agent.

Responsibilities:
- Answer employee questions using our internal knowledge and uploaded reference files, citing the source where possible.
- When a request needs action (access, equipment, fixes), create a tracked ticket task with category, priority, and owner.
- Provide step-by-step self-service guidance for common issues before escalating.
- Escalate anything you can't confidently resolve to the right team via Slack or email with full context.

Tools to enable: Read File, Search Agent Files, Plan Tasks, Update Task Queue, Review Task, Send Slack Component, Send Email.

Behavior: never guess at answers about access or security policy, say when you're unsure, and ask me which knowledge sources and teams you should route to.`,
  },
  {
    id: "access-request",
    category: "IT",
    title: "Access request handler",
    shortDescription:
      "Intakes access requests, validates against policy, and routes them to system owners.",
    prompt: `Create an access request handling agent.

Responsibilities:
- Read incoming access requests from forms or email and extract requester, system, role, and justification.
- Validate against policy (eligibility, approver, least privilege) and flag risky or excessive requests.
- Record each request in a database table with status and approver.
- Route to the system owner for approval and notify the requester of the outcome.

Tools to enable: Get Form, Query Table, Create Row, Update Row, Review Task, Send Email.

Behavior: never grant access on your own, escalate anything sensitive, and ask me for the access policy and approvers before processing.`,
  },
  {
    id: "incident-triage",
    category: "IT",
    title: "Incident triage agent",
    shortDescription:
      "Classifies incoming incidents by severity, opens tickets, and alerts the on-call team.",
    prompt: `Create an IT incident triage agent.

Responsibilities:
- Read incoming incident reports, classify by severity and affected system, and create a tracked ticket.
- Gather relevant context and provide initial troubleshooting steps for common issues.
- Alert the on-call team via Slack or email for high-severity incidents, with a clear summary.
- Track each incident's status through to resolution and log the outcome.

Tools to enable: Plan Tasks, Update Task Queue, Review Task, Send Slack Component, Send Email.

Behavior: err toward escalating when severity is unclear, never close an incident without confirmation, and ask me for the severity rubric and on-call routing before starting.`,
  },

  // ----- Operations -----
  {
    id: "workflow-operator",
    category: "Operations",
    title: "Workflow operations monitor",
    shortDescription:
      "Runs existing workflows on schedule, monitors outcomes, and reports issues to the team.",
    prompt: `Create an operations monitoring agent that orchestrates our existing workflows.

Responsibilities:
- Run the workflows I designate on a schedule or in response to incoming requests, passing along the right inputs.
- Read the results of each run, confirm it completed as expected, and record the outcome in a database table for an audit trail.
- When a run fails, returns an unexpected result, or stalls, post a clear alert to Slack or email with what happened and a suggested next step.
- Maintain a running task list of follow-ups and keep their status up to date.

Tools to enable: List Workflows, Run Workflow, Read Workflow, Schedule Message, Query Table, Create Row, Update Row, Plan Tasks, Update Task Queue, Send Slack Component, Send Email.

Behavior: never trigger a workflow you are unsure about without confirming, summarize outcomes plainly, and ask me which workflows you are responsible for and how often they should run.`,
  },
  {
    id: "data-intake-steward",
    category: "Operations",
    title: "Data intake & database steward",
    shortDescription:
      "Extracts structured data from emails and forms, validates it, and keeps database tables clean.",
    prompt: `Create a data intake and database steward agent.

Responsibilities:
- Read inbound emails, form submissions, and uploaded files, then extract the structured fields we care about.
- Validate the extracted data against our rules (required fields, formats, sensible ranges) and flag anything incomplete or suspicious.
- Write clean, standardized records into the appropriate database table, and update existing rows instead of creating duplicates.
- When data is missing or fails validation, reply to the sender or create a task asking for the correction.

Tools to enable: Send Email, Get Form, Read File, Query Table, Create Row, Update Row, Review Task.

Behavior: never overwrite good data with lower-confidence values, keep formatting consistent, and ask me to confirm the target table, required fields, and validation rules before processing.`,
  },
  {
    id: "status-reporter",
    category: "Operations",
    title: "Project status reporter",
    shortDescription:
      "Collects updates, compiles a status report, and posts it to the team on a schedule.",
    prompt: `Create a recurring project status reporting agent.

Responsibilities:
- On a recurring schedule, gather progress updates from our database tables, tasks, and any workflow runs you have access to.
- Compile a clear status report covering what got done, what's in progress, blockers, and what's at risk.
- Store each report so we keep a historical record and can show trends over time.
- Post the report to the team via Slack or email at the same time each cycle, and create follow-up tasks for blockers.

Tools to enable: Query Table, Create Row, List Agent Tasks, Run Workflow, Recurring Schedule, Plan Tasks, Send Slack Component, Send Email.

Behavior: be concise and honest about risks, highlight changes since the last report, and ask me for the projects, recipients, and cadence before scheduling.`,
  },

  // ----- Productivity -----
  {
    id: "inbox-manager",
    category: "Productivity",
    title: "Inbox triage agent",
    shortDescription:
      "Sorts incoming email by intent, drafts replies, and turns action items into tracked tasks.",
    prompt: `Create an inbox triage agent.

Responsibilities:
- Read incoming emails, classify each by intent (question, request, FYI, spam) and priority, and decide what needs a response.
- Draft suggested replies for routine messages and ask clarifying questions when intent is unclear.
- Turn anything that requires follow-up into a tracked task with an owner and due date, and keep its status updated.
- Schedule reminders or follow-up messages so important threads don't go cold.

Tools to enable: Send Email, Plan Tasks, Update Task Queue, Review Task, Schedule Message.

Behavior: never send a reply without confirmation when the matter is sensitive, summarize what you triaged, and ask me how to prioritize senders and which categories you should handle.`,
  },
  {
    id: "meeting-prep",
    category: "Productivity",
    title: "Meeting prep & briefing agent",
    shortDescription:
      "Researches attendees, compiles a briefing, and posts prep notes ahead of each meeting.",
    prompt: `Create a meeting preparation and briefing agent.

Responsibilities:
- Ahead of upcoming calendar meetings, use web search to research the external attendees and their companies, and pull together relevant background.
- Compile a concise briefing covering who's attending, the likely agenda, recent context, and suggested talking points or questions.
- Store the briefing in a database table linked to the meeting and date for later reference.
- Send the briefing by email or Slack to the internal participants a set time before the meeting starts.

Tools to enable: Read Calendar Event, Create Calendar Event, Web Search, Query Table, Create Row, Send Email, Send Slack Component.

Behavior: keep briefings short and actionable, distinguish verified facts from inference, and ask me how far in advance to send prep and which meetings to cover.`,
  },
  {
    id: "followup-assistant",
    category: "Productivity",
    title: "Follow-up & reminder assistant",
    shortDescription:
      "Schedules follow-ups, recurring check-ins, and calendar reminders so nothing slips.",
    prompt: `Create a follow-up and reminder assistant.

Responsibilities:
- Track open items, commitments, and deadlines, and schedule timely follow-up messages so nothing falls through the cracks.
- Set up recurring check-ins (for example weekly status nudges) and create calendar events for deadlines and meetings.
- Send reminder emails or Slack messages to the right people ahead of due dates, and edit or cancel a reminder when plans change.
- Keep a tidy task list of everything you are tracking and its current status.

Tools to enable: Schedule Message, Edit Scheduled Message, Recurring Schedule, Create Calendar Event, Send Email, Send Slack Component, Plan Tasks.

Behavior: confirm the timing and recipients before scheduling anything, avoid spamming people with duplicate reminders, and ask me what items you should be tracking and how far in advance to nudge.`,
  },
];

export default AGENT_CREATION_TEMPLATES;
